import { PING, PONG, type ServerMessage } from '@engineer-os/chat';
import { chatSocketUrl } from '../../lib/chat';
import type { Link } from './model';
import type { RoomView } from './types';

interface Events {
  hello(link: Link): void;
  message(link: Link, message: ServerMessage): void;
  patch(link: Link, value: Partial<RoomView>): void;
}

/** Owns exactly the socket and its timers; every callback reads current React state. */
export function createConnections(events: Events) {
  function stopRetry(link: Link) {
    if (link.retry !== null) window.clearTimeout(link.retry);
    link.retry = null;
  }
  function stopPing(link: Link) {
    if (link.ping !== null) window.clearInterval(link.ping);
    link.ping = null;
  }
  function schedule(link: Link) {
    if (link.done) return;
    stopRetry(link);
    const base = Math.min(30_000, 1_000 * 2 ** link.attempt++);
    link.retry = window.setTimeout(() => connect(link), Math.min(30_000, Math.round(base * (0.7 + Math.random() * 0.6))));
  }
  function connect(link: Link) {
    if (link.done || link.ws) return;
    stopRetry(link);
    events.patch(link, { status: 'connecting' });
    let socket: WebSocket;
    try { socket = new WebSocket(chatSocketUrl(link.room)); }
    catch { events.patch(link, { status: 'offline' }); schedule(link); return; }
    link.ws = socket;
    const current = () => !link.done && link.ws === socket;
    socket.onopen = () => {
      if (!current()) return;
      events.hello(link);
      stopPing(link);
      link.ping = window.setInterval(() => { if (current() && socket.readyState === WebSocket.OPEN) socket.send(PING); }, 25_000);
    };
    socket.onmessage = ev => {
      if (!current() || typeof ev.data !== 'string' || ev.data === PONG) return;
      let message: ServerMessage;
      try { message = JSON.parse(ev.data) as ServerMessage; } catch { return; }
      if (message && typeof message === 'object' && typeof message.t === 'string') events.message(link, message);
    };
    socket.onclose = () => {
      if (link.ws !== socket) return;
      link.ws = null;
      stopPing(link);
      if (link.done) return;
      events.patch(link, { status: 'offline', loadingEarlier: false,
        messages: link.view.messages.map(row => row.pending ? { ...row, pending: false, failed: true } : row) });
      schedule(link);
    };
    socket.onerror = () => {}; // close carries the actionable state.
  }
  function drop(link: Link) {
    link.done = true;
    stopRetry(link);
    stopPing(link);
    const socket = link.ws;
    link.ws = null;
    if (!socket) return;
    socket.onopen = socket.onmessage = socket.onclose = socket.onerror = null;
    socket.close(1000, 'leaving');
  }
  return { connect, drop };
}
